import React from 'react';
import { LineChart, YAxis, Path } from 'react-native-svg-charts';
import moment from 'moment';
import Theme from '../../theme';
import { View, Text, StyleSheet } from 'react-native';
import { G, Line, Circle, Rect, Defs, ClipPath, Text as SvgText } from 'react-native-svg';
import { BodyMetricGraphTypes } from 'fitbeat.models';
import { defaultScale, graphScale, graphScaleLine, fullScale, scaledRatio } from '../../utils/scaling';
import * as scale from 'd3-scale';
import theme from '../../theme';
import { calculateYAxesTickIndices, shouldRenderYAxisTickIndex } from '../utils';
import build from '../../config/build';

const yAxisWidth = defaultScale(38);
const circleRadius = graphScaleLine(5);
const { isElite } = build.variant;

const rangeColors = {
  acceptable: 'rgba(249, 166, 13, 0.1)',
  good: 'rgba(47, 248, 54, 0.1)',
};

const lineColors = {
  [rangeColors.acceptable]: '#F9A60D',
  [rangeColors.good]: '#2FF836',
};

const formatRanges = (ranges, graphType, maxMuscleForUser) => {
  if (!ranges) {
    return null;
  }

  const getMuscleMassEndRange = () => {
    return Math.max(ranges.Good, maxMuscleForUser);
  };

  const formattedRanges = [
    {
      color: rangeColors.acceptable,
      start: ranges.VeryLow,
      end: ranges.TooLow,
    },
    {
      color: rangeColors.good,
      start: ranges.TooLow,
      end:  maxMuscleForUser ? getMuscleMassEndRange() : ranges.Good,
    },
  ];

  if (graphType !== BodyMetricGraphTypes.MusclemassHistory) {
    formattedRanges.push({
     color: rangeColors.acceptable,
     start: ranges.Good,
     end: ranges.High,
   });
  }
  return formattedRanges;
};

const GraphText = ({x , y, data, unit}) => {
  const currentWeightIndex = data.length - 3;
  const labels = data.reduce((acc, cur, index) => {
    if (index === currentWeightIndex) {
      const diffInXpixels =  x(data[data.length - 2].date) - x(data[data.length - 3].date);
      if (diffInXpixels > graphScaleLine(35)) {
        return {...acc, [cur.date]: cur.value};
      }
    }
    if (cur.isTarget || cur.isMileStone) {
      return {...acc, [cur.date]: cur.value};
    }
    return acc;
  }, {});
  return Object.entries(labels).map(([key, value]) => {
    const labelValue = {
      x: x(new Date(key)) - graphScaleLine(8) ,
      y: y(value) - graphScaleLine(12),
    };
    const labelDate = {
      x: x(new Date(key)) - graphScaleLine(10),
      y: y(value) + graphScaleLine(17),
    };
    return(
    <G >
      <SvgText
        y={labelValue.y}
        x={labelValue.x}
        fill={theme.BLACK}
        stroke={theme.BLACK}
        strokeWidth={0.5}
        fontSize={theme.graphStyle.regular10.fontSize}
        fontFamily={theme.graphStyle.regular10.fontFamily}
        >
          {value}{unit}
      </SvgText>
      <SvgText
        y={labelDate.y}
        x={labelDate.x}
        fill={theme.BLACK}
        fontSize={theme.graphStyle.regular10.fontSize}
        fontFamily={theme.graphStyle.regular10.fontFamily}
        >
          {moment(key).format('D MMM')}
      </SvgText>
    </G>);
  });
};

const Circles = ({ x, y, data, showCirclesForAllPoints}) => {
  return data.map(({ value, date, isTarget, isMileStone }, index) => {
    const doesHaveTarget = data.some((dataPoint) => dataPoint.isTarget);
    const isTargetOrMilestone = isTarget || isMileStone;
    const isLastHistoricalPointBeforeMilestoneAndTarget = index === data.length - 3;
    const isLastHistoricalPointWhenNoTarget = index === data.length - 1;

    if ((doesHaveTarget && isLastHistoricalPointBeforeMilestoneAndTarget) ||
      isTargetOrMilestone ||
      isLastHistoricalPointWhenNoTarget ||
      showCirclesForAllPoints) {
      return (
        <Circle
          key={index}
          cx={x(date)}
          cy={y(value)}
          r={circleRadius}
          fill={isTarget ? '#CCCCCC' : isMileStone ? Theme.BLACK : Theme.PRIMARY}
        />
      );
    }
  });
};

const Clips = ({ x, width, clippingPoint, clipPathId, data, y }) => {
  const clippingPointX = x(clippingPoint);
  let clippingEndPointX = width;
  const dataPointIndex = data.findIndex(({date}) => {
    return date.getTime() === clippingPoint.getTime();
  });
  if (dataPointIndex < data.length - 1) {
    clippingEndPointX = x(data[dataPointIndex + 1].date);
  }
  return (
    <Defs key={'Clips'}>
      <ClipPath id={clipPathId}>
        <Rect
          x={clippingPointX + circleRadius}
          y={0}
          width={clippingEndPointX - clippingPointX}
          height={'100%'}
        />
      </ClipPath>
    </Defs>
  );
};

const ClipLine = ({ line , stroke, clipPathId}) => {
  return (
    <Path
      key={'line-1'}
      d={line}
      stroke={stroke}
      strokeWidth={graphScaleLine(3.5)}
      fill='none'
      clipPath={`url(#${clipPathId})`}
    />
  );
};

const Ranges = ({width, y, leftInset, rightInset, formattedRanges}) => {
  return <G>
    {
      formattedRanges.map((range, index) => {
        return (
        <G>
          <Rect
          x={0 + leftInset}
          y={y(range.end)}
          width={width - rightInset - leftInset}
          height={y(range.start) - y(range.end)}
          fill={range.color}
          />
          {index === 0 && <Line
            x1={0 + leftInset}
            x2={width - rightInset}
            y1={y(range.start)} y2={y(range.start)}
            stroke={lineColors[range.color]}
            strokeDasharray={[3, 3]}
          />}
          {index !== formattedRanges.length - 1 && index !== 0 &&
          <G>
            <Line
            x1={0 + leftInset}
            x2={width - rightInset}
            y1={y(range.start)} y2={y(range.start)}
            stroke={lineColors[range.color]}
            strokeDasharray={[3, 3]}
            />
            <Line
            x1={0 + leftInset}
            x2={width - rightInset}
            y1={y(range.end)} y2={y(range.end)}
            stroke={lineColors[range.color]}
            strokeDasharray={[3, 3]}
           />
          </G>
          }
          {index === formattedRanges.length - 1 }{
              <Line
              x1={0 + leftInset}
              x2={width - rightInset}
              y1={y(range.end)} y2={y(range.end)}
              stroke={lineColors[range.color]}
              strokeDasharray={[3, 3]}
            />
          }
        </G>);
      })
    }
  </G>;
};

export const BodyMetricGraph = ({
  verticalGridLines,
  minDate,
  maxDate,
  data,
  clippingPoint,
  curve,
  containerStyle,
  unit,
  graphType,
  ranges,
  leftInset = defaultScale(15),
  rightInset = defaultScale(22),
  topInset = graphScaleLine(22),
  bottomInset = graphScaleLine(25),
  showGraphText = false,
  showCirclesForAllPoints = false,
  useRanges = true,
}) => {
  const dataValues = data.map((dataPoint) => dataPoint.value);
  const domainRange = [minDate, maxDate];
  let maxMuscleForUser;
  if (graphType === BodyMetricGraphTypes.MusclemassHistory) {
    maxMuscleForUser = Math.max(...dataValues);
  }
  const formattedRanges = useRanges && formatRanges(ranges, graphType, maxMuscleForUser);
  const {customYAxesTickIndices, yMin, yMax, customTicks } = calculateYAxesTickIndices(
    dataValues, formattedRanges);
  const mileStone =  data.find((dataPoint) => dataPoint.isMileStone);
  const target =  data.find((dataPoint) => dataPoint.isTarget);
  const CustomGrid = ({ y, width }) => {
    const xTicksScale = scale
      .scaleTime()
      .domain(domainRange)
      .range([leftInset, width - rightInset]);
    return (
      <G>
        {customTicks.map((tick, index) => {
          if (shouldRenderYAxisTickIndex(customYAxesTickIndices, index)) {
            return <Line
              key={tick}
              x1={leftInset - 4}
              x2={leftInset + 4}
              y1={y(tick)}
              y2={y(tick)}
              stroke={'rgba(0,0,0,0.1)'}
            />;
          }
        },
        )}
        {verticalGridLines.map((value, index) => {
          return (
            <G>
              <Line
                key={index}
                y1={'0%'}
                y2={'100%'}
                x1={xTicksScale(value)}
                x2={xTicksScale(value)}
                stroke={'rgba(0,0,0,0.1)'}
              />
            </G>
          );
        })}
      </G>
    );
  };
  return (
    <View style={[styles.heightContainer, containerStyle]}>
      <View style={styles.graphContainer}>
        <YAxis
          style={styles.yAxis}
          data={customTicks}
          svg={{
            fontSize: Theme.graphStyle.regular10.fontSize,
            fontFamily: Theme.graphStyle.regular10.fontFamily,
            fill: Theme.BLACK,
            textAnchor: 'end',
            translateX: isElite ? 32 : 20,
          }}
          yMin={yMin}
          yMax={yMax}
          contentInset={{ top: topInset, bottom: bottomInset }}
          numberOfTicks={30}
          formatLabel= {(value, index) => {
            if (shouldRenderYAxisTickIndex(customYAxesTickIndices, index)) {
              return `${value}${unit}`;
            } else {
              return '';
            }
          }}
        />
        <LineChart
          style={styles.LineChart}
          data={data}
          yAccessor={({ item }) => item.value}
          xAccessor={({ item }) => item.date}
          contentInset={{
            left: leftInset,
            right: rightInset,
            top: topInset,
            bottom: bottomInset,
          }}
          numberOfTicks={30}
          xScale={scale.scaleTime}
          xMin={minDate}
          xMax={maxDate}
          yMin={yMin}
          yMax={yMax}
          svg={{
            stroke: Theme.PRIMARY,
            strokeWidth: graphScaleLine(3),
          }}
          curve={curve}
        >
          <CustomGrid />
          {clippingPoint && <Clips clippingPoint={clippingPoint} clipPathId={'last-body-scan-to-milestone'} />}
          {clippingPoint && <ClipLine clipPathId={'last-body-scan-to-milestone'} stroke={theme.BLACK}/> }
          {mileStone && <Clips clippingPoint={new Date(mileStone.date)} clipPathId={'milestone-to-target'} />}
          {mileStone && <ClipLine clipPathId={'milestone-to-target'} stroke={'#CCCCCC'}/> }
          <Circles showCirclesForAllPoints={showCirclesForAllPoints}/>
          { !target && formattedRanges &&
          <Ranges leftInset={leftInset} rightInset={rightInset} formattedRanges={formattedRanges}/>}
          { showGraphText && <GraphText unit={unit}/>}
        </LineChart>
      </View>
      <View style={[
        styles.labelContainer,
        {marginLeft: leftInset + defaultScale(yAxisWidth),
        marginRight: rightInset},
        ]}>
        {verticalGridLines.map((date, index) => {
          if (index < verticalGridLines.length - 1) {
            return (
              <Text
                style={{
                  width: `${100 / (verticalGridLines.length - 1)}%`,
                  textAlign: 'center',
                  ...theme.graphStyle.regular10,
                }}
              >
                {moment(date).format('MMM')}
              </Text>
            );
          }
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  heightContainer: {
    height: graphScale(100),
  },
  graphContainer: {
    flex: 1,
    flexDirection: 'row',
    paddingLeft: scaledRatio(0.1, 60),
    paddingRight: fullScale(5),
    paddingTop: fullScale(4),
  },
  yAxis: {
    width: defaultScale(yAxisWidth),
  },
  LineChart: {
    flex: 1,
  },
  labelContainer: {
    flexDirection: 'row',
    paddingLeft: scaledRatio(0.1, 60),
    paddingRight: fullScale(5),
    paddingBottom: fullScale(5),
  },
});
